/**
 * Where a validator's authority comes from, and the tags that record it.
 * `accepts` and `shape` declare what a validator takes; `ground` and
 * `endSeverity` turn a `Grounding` into its ADR-0032 tier. Every combinator
 * goes through one, so `boundGrounding.test.ts` can sweep the live registry.
 */
import { scalarSlotValue } from '../../../godot/variant/variantBool.js';
import { propertyError } from '../propertyError.js';
import { valueCode } from './codes.js';
/** The citation covering one end of a bound, if the grounding names it. */
function citeFor(g, end) {
    if (g === undefined)
        return undefined;
    return typeof g === 'string' ? g : g[end];
}
/** Both ends' citations, in min-then-max order, without the duplicate a shared one makes. */
function distinctCites(g) {
    const cites = [citeFor(g, 'min'), citeFor(g, 'max')].filter((cite) => cite !== undefined);
    return [...new Set(cites)];
}
/**
 * Tag a validator with what it accepts, for the generated `## Linting` table.
 * A bespoke validator needs it too: `validatorAccepts.test.ts` fails on an
 * untagged one.
 */
export function accepts(validator, description) {
    validator.accepts = description;
    return validator;
}
/**
 * `accepts`, plus the declaration that this validator rejects nothing but
 * malformed input: text the tokenizer refuses, or a whole value the slot never
 * converts. Every combinator, bespoke ones included, is a `shape` or carries a
 * `Grounding`, and `boundGrounding.test.ts` fails on one that is neither.
 */
export function shape(validator, description) {
    validator.formatOnly = true;
    return accepts(validator, description);
}
/**
 * Reject the special float literals a setter refuses, ahead of the range check,
 * which reports `Infinity > max` for the wrong reason and says nothing on NaN.
 * One implementation for both guards: the tag forwarding below breaks silently,
 * and two copies of it would drift.
 */
function withLiteralGuard(validator, name, cite, refuses, requirement) {
    const guarded = (key, value, line) => {
        const parsed = scalarSlotValue(value);
        if (parsed !== null && refuses(parsed)) {
            return propertyError(key, line, `Property '${name}' ${requirement}; Godot's setter refuses "${value.trim()}"`, valueCode(name));
        }
        return validator(key, value, line);
    };
    guarded.accepts = validator.accepts;
    // The bounded ends, their tiers and the numbers stay the inner validator's:
    // `hintImplementationParity` compares `bounds` against the engine's hint.
    guarded.tiers = validator.tiers;
    guarded.bounds = validator.bounds;
    // The tags a registry sweep navigates by: `everyValidator` descends `leaves`,
    // and the int-slot probe selects on `intSlot`. Dropping either shrinks the
    // sweep's population in silence.
    guarded.intSlot = validator.intSlot;
    guarded.leaves = validator.leaves;
    // Not `formatOnly`: `inf`/`nan` are legal literals, so this guard rejects a
    // real value. Keep both citations, as `ground` does: the literal guard and the
    // range guard are separate lines in the setter.
    const inner = validator.grounding?.cite;
    guarded.grounding = {
        // The inner kind wins where there is one. The literal branch is always an
        // error and says so directly, so stamping `enforced` unconditionally would
        // only mislabel a `hinted:` bound whose range branch still warns.
        kind: validator.grounding?.kind ?? 'enforced',
        cite: inner && inner !== cite ? `${cite}, ${inner}` : cite,
    };
    return guarded;
}
/** Reject all four non-finite spellings: `ERR_FAIL_COND(!is_finite(...))`. */
function withFiniteGuard(validator, name, cite) {
    return withLiteralGuard(validator, name, cite, (n) => !Number.isFinite(n), 'must be finite');
}
/**
 * Reject `nan` alone, for a setter guarded by `Math::is_nan` rather than by
 * `!is_finite`. `inf` and `-inf` reach the field unaltered there, so the finite
 * guard cannot stand in: it would reject two values Godot stores.
 */
function withNanGuard(validator, name, cite) {
    return withLiteralGuard(validator, name, cite, Number.isNaN, 'must not be NaN');
}
/** Apply `withFiniteGuard` only when the caller named a guard. */
export function maybeFinite(name, opts, validator) {
    return opts.finite ? withFiniteGuard(validator, name, opts.finite) : validator;
}
/** Apply `withNanGuard` only when the caller named the narrower guard alone. */
export function maybeNan(name, opts, validator) {
    return opts.nan && !opts.finite ? withNanGuard(validator, name, opts.nan) : validator;
}
/**
 * Tag a validator with its bounds, their tiers and its grounding, which
 * `boundGrounding.test.ts` sweeps in the live registry. A bound with neither
 * `enforced` nor `hinted` errors, and the sweep fails on it.
 */
export function ground(validator, opts, 
/**
 * The bound per end, by value, since a guard compares it against the engine's
 * `PROPERTY_HINT_RANGE`. An absent end is open, so `v.float('width')` is a
 * format check. `min`/`max` are the hint's outer ends, and `enforcedMin`/
 * `enforcedMax` the setter's own, which error from further out.
 */
bounds = {}) {
    const hasMin = bounds.min !== undefined;
    const hasMax = bounds.max !== undefined;
    // A setter end rejects real values just as a hint end does, so a validator
    // carrying only one is bounded and owes a citation.
    const isBounded = hasMin || hasMax || bounds.enforcedMin !== undefined || bounds.enforcedMax !== undefined;
    // Here, not in the range factories: every combinator passes through this
    // function, including those built from an inline arrow, such as `v.enumInt`.
    if (isBounded) {
        validator.bounds = bounds;
        // The tier of the outer end, the hint's, for a value between it and the
        // setter's own end, which always errors. An end with only a setter limit
        // errors too, so the sheet's out-of-range cell is never blank.
        const separate = { min: bounds.enforcedMin !== undefined, max: bounds.enforcedMax !== undefined };
        const tierFor = (end) => (end === 'min' ? hasMin : hasMax) ? endSeverity({ ...opts, ...bounds }, end) : 'error';
        validator.tiers = {
            ...(hasMin || separate.min ? { min: tierFor('min') } : {}),
            ...(hasMax || separate.max ? { max: tierFor('max') } : {}),
        };
    }
    // Every distinct citation, across both kinds and both ends, so the citation
    // sweep sees each: `extra_cull_margin` grounds its ends differently, and
    // `Control.anchors_preset` has a distinct enforced cite per end.
    const enforced = distinctCites(opts.enforced);
    const hinted = distinctCites(opts.hinted);
    if (enforced.length > 0) {
        const both = [...enforced, ...hinted.filter((cite) => !enforced.includes(cite))];
        validator.grounding = { kind: 'enforced', cite: both.join(', ') };
    }
    else if (hinted.length > 0) {
        validator.grounding = { kind: 'hinted', cite: hinted.join(', ') };
    }
    // An unbounded numeric combinator rejects only what is not a number, which
    // is the same class of rejection every `shape` makes.
    if (!isBounded)
        validator.formatOnly = true;
    return validator;
}
/**
 * Whether the hint's end at `end` describes a band any value can land in.
 * `exclusive` moves the endpoint, not the band: `[at, min)` and `(at, min)` are
 * both non-empty exactly when `at < min`. `lintCoverage.mjs` computes the same for
 * the sheet, and `boundGrounding.tiers.test.ts` sweeps for disagreement.
 */
function outerEndIsReachable(bounds, end) {
    const hintEnd = end === 'min' ? bounds.min : bounds.max;
    if (hintEnd === undefined)
        return false;
    const setterEnd = end === 'min' ? bounds.enforcedMin : bounds.enforcedMax;
    if (setterEnd === undefined)
        return true;
    return end === 'min' ? setterEnd.at < hintEnd : setterEnd.at > hintEnd;
}
/**
 * Severity for one end of a bound (ADR-0032): `warning` when only a hint names
 * it, `error` when the setter does. An end neither names errors.
 */
export function endSeverity(opts, end) {
    // A setter limit strictly further out than the hint's end leaves the band
    // between them to the hint, which warns. At or inside the hint's end there is
    // no such band, so the end keeps the enforced tier.
    const setterEnd = end === 'min' ? opts.enforcedMin : opts.enforcedMax;
    const separateEnforcedEnd = setterEnd !== undefined && outerEndIsReachable(opts, end);
    if (!separateEnforcedEnd && citeFor(opts.enforced, end))
        return 'error';
    return citeFor(opts.hinted, end) ? 'warning' : 'error';
}
//# sourceMappingURL=grounding.js.map